// One entry per piece of equipment (#1906), for every device picker and list.
// A site's devices come from two reads: the devices the site holds
// (`GET /sites/{id}/devices`) and the site's plan
// (`GET /sites/{id}/planner/devices`). A planned device bound to a serial is
// the same piece of equipment as the held device with that serial: its port
// intent lives on the planned device and the held device reads it through the
// binding, one source (ZENITH.md §11.3, ADR-0069 decision 1). So the two rows
// are one entry here, and no picker offers the piece twice.
//
// **The groups.** A held device, with the planned device bound to it if there
// is one, is *at the site*. A planned device with no serial is *not installed
// yet*. A planned device bound to a serial the site's read did not return is
// shown once, marked *bound · not at this site*.
//
// **The label** is the piece's friendly name, the first of these that exists
// (ruled 2026-10-20 on #1906): the bound planned device's name; the device's
// own name; otherwise *model · location*. A name that is just the serial
// counts as no name, so a device enrolled under its serial falls through to
// its model. The serial is a separate part, drawn as a muted suffix, and is
// never shown twice: where the label falls all the way back to the serial,
// there is no suffix.
//
// Pure, and the only place this is decided: a page that names or lists the
// site's equipment takes its entries or its labels from here.
import type { components } from "../../api/schema";
import type { Device } from "./commands";

export type PlannedDevice = components["schemas"]["PlannedDevice"];

/** Where an entry stands: held by the site, planned and not installed, or
 * bound to a serial the site does not hold. */
export type EquipmentGroup = "site" | "not-installed" | "elsewhere";

/** Each group's heading, in the order the groups are listed. */
export const GROUP_WORDS: Record<EquipmentGroup, string> = {
  site: "At the site",
  "not-installed": "Not installed yet",
  elsewhere: "Bound · not at this site",
};

/** A piece of equipment's name as drawn: the label, and the serial as a
 * separate muted part when the label is not already the serial. `named` says
 * whether the label is a name someone gave (the planned device's or the
 * device's own) rather than the model fallback. */
export type EquipmentLabel = { label: string; serial?: string; named: boolean };

/** One piece of equipment: the held device where the site holds it, the
 * planned device bound to it or waiting for it, which group it is in, and
 * its label. `key` is unique within one `equipmentOf` answer and is the
 * value a picker carries: `serial:<serial>` for a held device, and
 * `planned:<id>` for a planned device the site does not hold. */
export type Equipment = {
  key: string;
  held?: Device;
  planned?: PlannedDevice;
  group: EquipmentGroup;
} & EquipmentLabel;

/** Whether a name is just the serial written out — as it is, a device's
 * controller name, or an enrolment's planned name (ZENITH.md §13.1) — which
 * counts as no name. Case is not a difference, and nor are the separators of
 * a hexadecimal serial written as a MAC (`24:34:08:02:3e:b8`); a name that
 * only shares a serial's letters and digits (`AP-0203` for `ap0203`) is a
 * name. */
export function isSerialName(name: string | undefined, serial: string | undefined): boolean {
  if (!name || !name.trim()) return true;
  if (!serial) return false;
  const n = name.trim().toLowerCase(), s = serial.toLowerCase();
  if (n === s) return true;
  return /^[0-9a-f]+$/.test(s) && /^[0-9a-f]{2}([:.-]?[0-9a-f]{2})+$/.test(n) && n.replace(/[:.-]/g, "") === s;
}

/** The model a held device is: the catalogue's answer where it resolved one,
 * else the string the device declares. */
function heldModel(d: Device): string | undefined {
  const m = d.catalogue_model;
  return (m?.state === "recognised" ? m.model : undefined) ?? d.manufacturer ?? undefined;
}

/** A piece of equipment's label, from the held device, the planned device
 * bound to it or waiting for it, or both. `where` names a planned device's
 * location by its id; left out, a label carries no location. */
export function equipmentLabel(held: Device | undefined, planned: PlannedDevice | undefined, where?: (locationId: string) => string | undefined): EquipmentLabel {
  const serial = held?.serial ?? planned?.serial;
  const named = (name: string | undefined) => name && !isSerialName(name, serial) ? name.trim() : undefined;
  const given = named(planned?.name) ?? named(held?.name);
  const withSerial = (label: string, isNamed: boolean): EquipmentLabel => ({ label, named: isNamed, ...(serial && !isSerialName(label, serial) ? { serial } : {}) });
  if (given) return withSerial(given, true);
  const model = planned?.model ?? (held ? heldModel(held) : undefined);
  const location = planned?.location_id && where ? where(planned.location_id) : undefined;
  const fallback = [model, location].filter((s): s is string => !!s).join(" · ");
  if (fallback) return withSerial(fallback, false);
  return { label: serial ?? planned?.name ?? "", named: false };
}

/** A label as one line of plain text — an `<option>`, an `aria-label`, a
 * sentence — with the serial once, after the label. */
export function equipmentText(l: EquipmentLabel): string {
  return l.serial ? `${l.label} · ${l.serial}` : l.label;
}

/** A label inside a sentence or on a button — *Declare SW-01 Ethernet3 ↔ …* —
 * where a given name is enough to say which piece it is: the name alone, and
 * the model fallback with its serial, since a model names no one piece. */
export function equipmentWords(l: EquipmentLabel): string {
  return l.named ? l.label : equipmentText(l);
}

const byLabel = (a: Equipment, b: Equipment) => a.label.localeCompare(b.label, undefined, { numeric: true }) || a.key.localeCompare(b.key);

/** The site's equipment, one entry per piece: each held device with the
 * planned device bound to it merged in, then the planned devices not
 * installed yet, then those bound to a serial the site does not hold — each
 * group by label. `family` narrows both reads to switches or to access
 * points; `where` names a planned device's location. */
export function equipmentOf(held: Device[], planned: PlannedDevice[], opts: { family?: "ap" | "switch"; where?: (locationId: string) => string | undefined } = {}): Equipment[] {
  const { family, where } = opts;
  const devices = family ? held.filter((d) => d.category === family) : held;
  const plans = family ? planned.filter((p) => p.family === family) : planned;
  // A binding is once per serial (ADR-0068); the first row bound to a serial
  // is the one merged, and the serial is compared as the device reports it.
  const boundTo = new Map<string, PlannedDevice>();
  for (const p of plans) if (p.serial && !boundTo.has(p.serial.toLowerCase())) boundTo.set(p.serial.toLowerCase(), p);
  const heldSerials = new Set(devices.map((d) => d.serial.toLowerCase()));
  const entry = (group: EquipmentGroup, d: Device | undefined, p: PlannedDevice | undefined): Equipment => ({
    key: d ? `serial:${d.serial}` : `planned:${p!.id}`,
    ...(d ? { held: d } : {}),
    ...(p ? { planned: p } : {}),
    group,
    ...equipmentLabel(d, p, where),
  });
  const site = devices.map((d) => entry("site", d, boundTo.get(d.serial.toLowerCase())));
  const merged = new Set(site.flatMap((e) => e.planned ? [e.planned.id] : []));
  const waiting = plans.filter((p) => !p.serial).map((p) => entry("not-installed", undefined, p));
  const elsewhere = plans.filter((p) => p.serial && !merged.has(p.id) && !heldSerials.has(p.serial.toLowerCase())).map((p) => entry("elsewhere", undefined, p));
  return [...site.sort(byLabel), ...waiting.sort(byLabel), ...elsewhere.sort(byLabel)];
}

/** Each serial's entry, for a page that starts from a serial — a typed one,
 * a link's end, a port's far end — rather than from the site's list:
 * undefined for a serial neither read holds. */
export function entryBySerial(devices: Device[], planned: PlannedDevice[] = []): (serial: string) => Equipment | undefined {
  const by = new Map<string, Equipment>();
  for (const e of equipmentOf(devices, planned)) {
    const serial = e.held?.serial ?? e.planned?.serial;
    if (serial) by.set(serial.toLowerCase(), e);
  }
  return (serial) => by.get(serial.toLowerCase());
}

/** A name for each serial, for the places a link's or a port's far end
 * carries only a serial: the label where it is a given name, and the label
 * with the serial where it fell back to the model. A serial the reads do not
 * hold is said as itself — a link outlives an unclaim, and says so by the
 * serial. */
export function namer(devices: Device[], planned: PlannedDevice[] = []): (serial: string) => string {
  const of = entryBySerial(devices, planned);
  return (serial) => {
    const e = of(serial);
    return e ? equipmentWords(e) : serial;
  };
}

/** A label as drawn in a table or a list: the label, then the serial muted,
 * once. `serial={false}` leaves the suffix out where a column of its own
 * already says the serial. */
export function EquipmentName({ of, serial = true }: { of: EquipmentLabel; serial?: boolean }) {
  return <>{of.label}{serial && of.serial && <span className="muted"> · {of.serial}</span>}</>;
}

/** The id a link's end, a picker or a filter names an entry by: the held
 * device's serial, or `planned:<id>` for a planned device the site does not
 * hold — the ids `targetOf` (`Topology.tsx`) reads. */
export const endIdOf = (e: Equipment): string => e.held ? e.held.serial : `planned:${e.planned!.id}`;

/** A picker's options, one per piece of equipment, under each group's
 * heading where there is more than one group. The text is the label with
 * the serial once; `detail` adds a word after it, such as what kind of
 * device it is. */
export function EquipmentOptions({ entries, idOf = (e: Equipment) => e.key, detail }: {
  entries: Equipment[];
  idOf?(e: Equipment): string;
  detail?(e: Equipment): string | undefined;
}) {
  const option = (e: Equipment) => {
    const more = detail?.(e);
    return <option key={e.key} value={idOf(e)}>{equipmentText(e)}{more ? ` · ${more}` : ""}</option>;
  };
  const groups = (Object.keys(GROUP_WORDS) as EquipmentGroup[]).map((g) => ({ g, of: entries.filter((e) => e.group === g) })).filter((x) => x.of.length > 0);
  if (groups.length <= 1) return <>{entries.map(option)}</>;
  return <>{groups.map(({ g, of }) => <optgroup key={g} label={GROUP_WORDS[g]}>{of.map(option)}</optgroup>)}</>;
}
